import React, { useMemo, useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ChevronLeft, ArrowUpRight, AlertTriangle, ShieldCheck, Calendar, Tag, Coins, TrendingUp,
  Clock, Banknote, FileCheck2, Bookmark, Sparkles, Info,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { BrandLogo } from "@/components/BrandLogo";
import { BrandStack } from "@/components/BrandStack";
import { StatusBadge } from "@/components/StatusBadge";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { format } from "@/lib/brand-tokens";
import {
  deals, getDealById, getBrandById, getValidationRuleById,
  DEMO_MEMBER, getMemberDashboardStats, createReservation, dealDefaultFAQ,
  getReservationsByMember,
} from "@/lib/mock-data";
import { parseRateChange } from "@/lib/dataAdapter";
import { useStoreVersion } from "@/hooks/useStore";
import { toast } from "sonner";

// MEM-03 — Deal detail. Multi-brand deals show each brand's economics
// independently and the reserve sidebar requires picking a brand first.
export default function MemberDealDetailPage() {
  // Subscribe to store mutations so the reserve form re-reads the member's
  // cumulative face on this deal+brand after each reservation.
  useStoreVersion();
  const { dealId } = useParams();
  const navigate = useNavigate();
  const deal = getDealById(dealId) || deals.find((d) => d.status === "Published");
  const primaryCfg = (deal.brandConfigs || []).find(c => c.isPrimary) || (deal.brandConfigs || [])[0];
  const stats = getMemberDashboardStats(DEMO_MEMBER.id);
  const days = Math.max(0, Math.ceil((new Date(deal.deadline) - Date.now()) / 86_400_000));
  const closesSoon = days <= 3;

  // Selected brand for the reserve sidebar (multi-brand only; defaults to primary).
  const [selectedBrandId, setSelectedBrandId] = useState(primaryCfg?.brandId || "");
  const selectedCfg = (deal.brandConfigs || []).find(c => c.brandId === selectedBrandId) || primaryCfg;
  const selectedBrand = getBrandById(selectedCfg?.brandId);

  // Reserve form scoped to the selected brand's economics.
  const [face, setFace] = useState(selectedCfg?.minFace || 25);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // When the selected brand changes, reset the face to that brand's min.
  useEffect(() => { setFace(selectedCfg?.minFace || 25); }, [selectedBrandId]); // eslint-disable-line

  const remaining = (selectedCfg?.capTotal || 0) - (selectedCfg?.capUsed || 0);
  const expectedPayout = (face * (selectedCfg?.rate || 0)) / 100;

  // Cumulative face this member has ALREADY reserved on this deal+brand
  // (excluding terminal cancellations). The per-member cap is per deal+brand.
  const memberReserved = getReservationsByMember(DEMO_MEMBER.id)
    .filter(r => r.dealId === deal.id
              && r.brandId === selectedCfg?.brandId
              && r.status !== "Cancelled")
    .reduce((sum, r) => sum + (r.faceValueReserved || 0), 0);

  const perMemberCap   = selectedCfg?.perMemberCap || 0;
  const perMemberLeft  = Math.max(0, perMemberCap - memberReserved);
  const exceedsRemaining  = face > remaining;
  const exceedsPerMember  = (memberReserved + face) > perMemberCap;
  const exceedsCapacity   = face > stats.availableCapacity;
  const belowMin          = face < (selectedCfg?.minFace || 0);
  // Note: selectedCfg.maxFace is the per-card denomination ceiling (e.g. $500
  // for Home Depot), NOT a reservation-total ceiling. A reservation can sum
  // multiple cards up to perMemberCap.
  const cantReserve       = exceedsRemaining || exceedsPerMember || belowMin;
  const rateChange        = parseRateChange(deal.internalNote);

  const faq = useMemo(() => (
    Array.isArray(deal.faqOverride) && deal.faqOverride.length ? deal.faqOverride : dealDefaultFAQ
  ), [deal.faqOverride]);

  const validationGroups = useMemo(() => {
    const rules = (deal.validationRuleIds || []).map(getValidationRuleById).filter(Boolean);
    return {
      mustPass:   rules.filter(r => r.severity === "reject"),
      shouldPass: rules.filter(r => r.severity !== "reject"),
    };
  }, [deal.validationRuleIds]);

  // Aggregate fill % for the hero strip (sum across all brands).
  const aggCapTotal = (deal.brandConfigs || []).reduce((a, c) => a + c.capTotal, 0);
  const aggCapUsed  = (deal.brandConfigs || []).reduce((a, c) => a + c.capUsed, 0);
  const aggFillPct  = aggCapTotal > 0 ? Math.round((aggCapUsed / aggCapTotal) * 100) : 0;
  const aggRemaining = aggCapTotal - aggCapUsed;

  // Headline rate for hero: uniform → single number; varies → "min–max%".
  const rates = (deal.brandConfigs || []).map(c => c.rate);
  const heroRate = deal.ratesUniform ? rates[0] : `${Math.min(...rates)}–${Math.max(...rates)}`;

  const confirm = () => {
    setConfirmOpen(false);
    try {
      createReservation(DEMO_MEMBER.id, deal.id, face, selectedCfg.brandId);
      toast.success(`Reserved ${format.currency(face)} on ${selectedBrand?.name}`, {
        description: `Expected payout: ${format.currency(expectedPayout)}. Submit cards before ${format.date(deal.deadline)}.`,
      });
      setTimeout(() => navigate("/member/reservations"), 800);
    } catch (e) {
      // Defensive: mutator rejected (e.g. cap exceeded mid-flight). Surface clearly.
      toast.error("Reservation rejected", { description: e?.message || "Please review your inputs." });
    }
  };

  return (
    <div data-testid="member-deal-detail-page" className="space-y-6 pb-12">
      <Link to="/member/deals" className="inline-flex items-center gap-1.5 text-caption text-ink-subtle hover:text-ink">
        <ChevronLeft className="size-3.5" /> Browse deals
      </Link>

      {/* ─── HERO ──────────────────────────────────────────────────────── */}
      <header
        data-testid="deal-hero"
        className="relative overflow-hidden bg-gradient-to-br from-brand-900 via-brand-800 to-brand-700 text-white rounded-2xl px-7 py-8 sm:px-10 sm:py-10"
      >
        <div aria-hidden className="pointer-events-none absolute inset-0 opacity-[0.07] mix-blend-overlay"
             style={{ backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' seed='3'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.6'/></svg>\")" }} />

        <div className="relative flex flex-wrap items-center justify-between gap-6">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-x-3 gap-y-1 flex-wrap">
              <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-white/10 border border-white/15 text-caption font-semibold uppercase tracking-wide">
                <Sparkles className="size-3" />
                <span>{deal.isMultiBrand ? "Multi-brand deal" : "Active buy"}</span>
                {deal.isFeatured && <span className="ml-1 text-amber-200">· Featured</span>}
              </div>
              {(() => {
                const names = deal.isMultiBrand
                  ? deal.brandIds.map((id) => getBrandById(id)?.name).filter(Boolean).join(" + ")
                  : getBrandById(deal.brandId)?.name;
                return deal.isMultiBrand && names ? (
                  <span className="text-caption text-white/80 font-medium">· {names}</span>
                ) : null;
              })()}
            </div>
            <h1 className="font-brand mt-4 text-4xl sm:text-5xl font-bold leading-[1.08] break-words">
              {deal.name}
            </h1>
            {!deal.isMultiBrand && (
              <p className="mt-3 text-white/80 text-body-sm sm:text-body">{getBrandById(deal.brandId)?.name}</p>
            )}
          </div>

          <div className="flex items-center gap-5 shrink-0 self-center">
            <div className="text-right">
              <div className="font-brand text-5xl sm:text-6xl font-bold leading-none tabular-nums">
                {heroRate}<span className="text-2xl sm:text-3xl text-white/70 align-top ml-1">%</span>
              </div>
              <div className="mt-1 text-caption text-white/60 uppercase tracking-wide">
                {deal.ratesUniform ? "of face value" : "rate range"}
              </div>
            </div>
            <BrandStack brandIds={deal.brandIds} size="lg" />
          </div>
        </div>

        {/* Hero stats strip — Per-member tile reflects the selected brand */}
        <div className="relative mt-8 grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/10 rounded-lg overflow-hidden border border-white/10">
          <HeroStat icon={<Clock className="size-3.5" />} label="Closes in" value={`${days} day${days === 1 ? "" : "s"}`} urgent={closesSoon} />
          <HeroStat
            icon={<Tag className="size-3.5" />}
            label={deal.isMultiBrand ? `${selectedBrand?.name || ""} · face range` : "Face value range"}
            value={`$${selectedCfg?.minFace ?? deal.minFace}–$${selectedCfg?.maxFace ?? deal.maxFace}`}
          />
          <HeroStat
            icon={<ShieldCheck className="size-3.5" />}
            label={deal.isMultiBrand ? `${selectedBrand?.name || ""} · per-member cap` : "Per-member cap"}
            value={format.currency(selectedCfg?.perMemberCap || 0)}
          />
          <HeroStat
            icon={<Banknote className="size-3.5" />}
            label={deal.isMultiBrand ? "Total cap remaining (all brands)" : "Cap remaining"}
            value={format.currency(aggRemaining)}
          />
        </div>
      </header>

      {/* Rate-bumped banner (member-facing) */}
      {rateChange && rateChange.retroactive && (
        <div data-testid="member-rate-change-banner" className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 flex items-start gap-3">
          <TrendingUp className="size-5 text-emerald-700 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-body-sm font-semibold text-ink">
              Good news — rate bumped to <span className="tabular-nums">{rateChange.to}%</span>
            </div>
            <p className="mt-0.5 text-caption text-ink-muted leading-relaxed">
              We raised the rate from <span className="tabular-nums">{rateChange.from}%</span> to <span className="tabular-nums">{rateChange.to}%</span>.
              If you already have a reservation on this deal, it&rsquo;s being honoured at the new rate — your expected payout went up.
            </p>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* ─── MAIN COLUMN ────────────────────────────────────────────── */}
        <section className="lg:col-span-2 space-y-6">

          {/* About this deal */}
          <ContentCard data-testid="about-section" eyebrow="About" title="Why we're running this deal">
            {deal.whyBuying ? (
              <p className="text-body text-ink-muted leading-relaxed whitespace-pre-line">{deal.whyBuying}</p>
            ) : (
              <p className="text-body-sm text-ink-hint italic">No description yet — admins can add one from the deal config.</p>
            )}

            {deal.publicNote && (
              <div className="mt-5 bg-brand-50/60 border border-brand-100 rounded-md p-4 flex items-start gap-3">
                <Info className="size-4 text-brand-700 mt-0.5 shrink-0" />
                <div>
                  <div className="text-caption font-semibold text-brand-900 mb-0.5">From the desk</div>
                  <p className="text-caption text-ink-muted leading-relaxed">{deal.publicNote}</p>
                </div>
              </div>
            )}

            {/* Aggregate cap progress (across all brands) */}
            <div className="mt-6 pt-5 border-t border-line">
              <div className="flex items-center justify-between text-caption mb-2">
                <span className="text-ink-muted">{deal.isMultiBrand ? "Total cap across all brands" : "Total cap usage"}</span>
                <span className="text-ink tabular-nums">
                  {format.currency(aggCapUsed)} used / {format.currency(aggCapTotal)} · {aggFillPct}%
                </span>
              </div>
              <Progress value={aggFillPct} className="h-2" />
            </div>
          </ContentCard>

          {/* Per-brand breakdown — only for multi-brand deals */}
          {deal.isMultiBrand && (
            <ContentCard data-testid="per-brand-breakdown" eyebrow="Per brand" title="Each brand has its own rate & cap">
              <p className="text-caption text-ink-subtle mb-5">
                Multi-brand deals are made up of separate per-brand pools. Pick a brand in the reserve panel to lock in that brand&rsquo;s rate.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {deal.brandConfigs.map((cfg) => {
                  const b = getBrandById(cfg.brandId);
                  const cfgRemaining = cfg.capTotal - cfg.capUsed;
                  const cfgFillPct = cfg.capTotal > 0 ? Math.round((cfg.capUsed / cfg.capTotal) * 100) : 0;
                  return (
                    <button
                      key={cfg.brandId}
                      type="button"
                      onClick={() => setSelectedBrandId(cfg.brandId)}
                      data-testid={`brand-pool-${cfg.brandId}`}
                      className={`text-left border rounded-lg p-4 transition-all ${
                        selectedBrandId === cfg.brandId
                          ? "border-brand-700 bg-brand-50/50 ring-1 ring-brand-100"
                          : "border-line bg-surface hover:border-ink-hint"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-2.5 min-w-0">
                          {b && <BrandLogo brand={b} size="sm" />}
                          <div className="min-w-0">
                            <div className="text-body-sm font-semibold text-ink truncate">{b?.name || cfg.brandId}</div>
                            {cfg.isPrimary && <div className="text-[10px] font-bold uppercase tracking-wide text-brand-700">Primary brand</div>}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="font-brand text-2xl font-bold text-brand-700 leading-none tabular-nums">{cfg.rate}%</div>
                          <div className="text-caption text-ink-subtle">of face</div>
                        </div>
                      </div>
                      <dl className="mt-4 grid grid-cols-2 gap-y-2 text-caption">
                        <dt className="text-ink-hint">Face range</dt>
                        <dd className="text-ink font-semibold tabular-nums text-right">${cfg.minFace}–${cfg.maxFace}</dd>
                        <dt className="text-ink-hint">Per-member cap</dt>
                        <dd className="text-ink font-semibold tabular-nums text-right">{format.currency(cfg.perMemberCap)}</dd>
                      </dl>
                      <div className="mt-3">
                        <div className="flex items-center justify-between text-caption mb-1">
                          <span className="text-ink-muted">Cap remaining</span>
                          <span className="text-ink font-semibold tabular-nums">{format.currency(cfgRemaining)} of {format.currency(cfg.capTotal)}</span>
                        </div>
                        <Progress value={cfgFillPct} className="h-1.5" />
                      </div>
                    </button>
                  );
                })}
              </div>
            </ContentCard>
          )}

          {/* How it works */}
          <ContentCard eyebrow="How it works" title="From reservation to payout">
            <ol className="mt-1 space-y-5">
              <Step n={1} icon={<Bookmark className="size-4" />} title="Reserve your share"
                    body={
                      deal.isMultiBrand
                        ? `Pick a brand, then lock in your slot at that brand's rate. Each brand has its own cap.`
                        : `Lock in your slot at ${primaryCfg?.rate}% rate. You can reserve up to ${format.currency(primaryCfg?.perMemberCap || 0)} on this deal.`
                    } />
              <Step n={2} icon={<FileCheck2 className="size-4" />} title="Submit your cards"
                    body={`Paste card numbers and PINs before ${format.date(deal.deadline)}. We OCR-check each card against this deal's validation rules.`} />
              <Step n={3} icon={<ShieldCheck className="size-4" />} title="We validate & approve"
                    body="Approved cards lock your payout. Rejected cards are flagged with a reason and don't count against your reservation." />
              <Step n={4} icon={<Banknote className="size-4" />} title="ACH lands in your bank" last
                    body={`Payment is initiated on the deal's terms: ${deal.paymentTerms}. Funds typically land 1–2 business days after.`} />
            </ol>
          </ContentCard>

          {/* Card requirements */}
          <ContentCard eyebrow="Card requirements" title="What we'll check on each card">
            <p className="text-caption text-ink-subtle mb-5">
              Cards are checked against these rules at approval time. <span className="font-semibold text-ink">Must-pass</span> rules will reject the card; <span className="font-semibold text-ink">should-pass</span> rules flag for manual review.
            </p>
            {validationGroups.mustPass.length > 0 && (
              <RuleGroup heading="Must pass" tone="red" rules={validationGroups.mustPass} />
            )}
            {validationGroups.shouldPass.length > 0 && (
              <div className={validationGroups.mustPass.length > 0 ? "mt-6" : ""}>
                <RuleGroup heading="Should pass (flagged on fail)" tone="amber" rules={validationGroups.shouldPass} />
              </div>
            )}
            {validationGroups.mustPass.length === 0 && validationGroups.shouldPass.length === 0 && (
              <p className="text-caption text-ink-hint italic">No specific rules set for this deal — standard checks apply.</p>
            )}
          </ContentCard>

          {/* FAQ */}
          <ContentCard eyebrow="FAQ" title="Common questions" data-testid="deal-faq">
            <Accordion type="single" collapsible className="-mx-2">
              {faq.map((item, i) => (
                <AccordionItem key={i} value={`q-${i}`} className="border-line">
                  <AccordionTrigger className="px-2 hover:no-underline text-left text-body-sm font-semibold text-ink hover:text-brand-700 [&[data-state=open]>svg]:text-brand-700">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="px-2 text-caption text-ink-muted leading-relaxed">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </ContentCard>
        </section>

        {/* ─── RESERVE SIDEBAR ────────────────────────────────────────── */}
        <aside className="lg:col-span-1">
          <div className="bg-surface border border-line rounded-xl p-6 lg:sticky lg:top-20">
            <div className="eyebrow mb-2">Reserve</div>
            <h2 className="font-brand text-h2 text-ink">Lock your share</h2>
            <p className="mt-1 text-caption text-ink-subtle leading-relaxed">
              You&rsquo;ll have until <span className="font-semibold text-ink">{format.date(deal.deadline)}</span> to submit cards against this reservation.
            </p>

            {/* Brand picker — multi-brand only */}
            {deal.isMultiBrand && (
              <div className="mt-5" data-testid="reserve-brand-picker">
                <Label className="text-caption font-semibold text-ink mb-2 block">Which brand?</Label>
                <div className="grid gap-1.5">
                  {deal.brandConfigs.map((cfg) => {
                    const b = getBrandById(cfg.brandId);
                    const active = selectedBrandId === cfg.brandId;
                    return (
                      <button
                        key={cfg.brandId}
                        type="button"
                        onClick={() => setSelectedBrandId(cfg.brandId)}
                        data-testid={`reserve-brand-${cfg.brandId}`}
                        className={`flex items-center gap-2.5 px-3 py-2 rounded-md border text-left transition-all ${
                          active
                            ? "border-brand-700 bg-brand-50/50 ring-1 ring-brand-100"
                            : "border-line bg-surface hover:border-ink-hint"
                        }`}
                      >
                        {b && <BrandLogo brand={b} size="xs" />}
                        <span className="text-body-sm font-medium text-ink flex-1 min-w-0 truncate">{b?.name || cfg.brandId}</span>
                        <span className="text-caption text-brand-700 font-bold tabular-nums">{cfg.rate}%</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="mt-5 space-y-4">
              <div>
                <Label className="text-caption font-semibold text-ink mb-1.5 block">Face value to reserve</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-hint text-body-sm">$</span>
                  <Input
                    type="number"
                    min={selectedCfg?.minFace}
                    max={Math.min(remaining, perMemberLeft)}
                    step={50}
                    value={face}
                    onChange={(e) => setFace(Number(e.target.value || 0))}
                    className="pl-7 h-11 text-body font-semibold tabular-nums"
                    data-testid="reserve-amount-input"
                  />
                </div>
                <p className="mt-1.5 text-caption text-ink-hint">
                  Min ${selectedCfg?.minFace} ·{" "}
                  {memberReserved > 0
                    ? <>You&rsquo;ve reserved {format.currency(memberReserved)} of {format.currency(perMemberCap)} ({format.currency(perMemberLeft)} left)</>
                    : <>Per-member cap {format.currency(perMemberCap)}</>}
                </p>
              </div>

              <div className="bg-brand-50/60 border border-brand-100 rounded-md p-4 space-y-2.5">
                <Row label="Brand" value={selectedBrand?.name || "—"} small />
                <Row label="Reserved" value={format.currency(face)} />
                <Row label="Rate" value={`${selectedCfg?.rate || 0}%`} />
                <div className="border-t border-brand-100 pt-2.5">
                  <Row label="Expected payout" value={format.currency(expectedPayout)} accent />
                </div>
              </div>

              {(belowMin || exceedsRemaining || exceedsPerMember) && (
                <div className="flex items-start gap-2 px-3 py-2.5 bg-red-50 border border-red-200 rounded-md text-caption text-red-700">
                  <AlertTriangle className="size-3.5 mt-0.5 shrink-0" />
                  <span>
                    {belowMin && `Minimum reservation on ${selectedBrand?.name} is $${selectedCfg?.minFace}.`}
                    {exceedsRemaining && `Only ${format.currency(remaining)} remaining in ${selectedBrand?.name}'s pool.`}
                    {exceedsPerMember && (memberReserved > 0
                      ? `You've already reserved ${format.currency(memberReserved)} on ${selectedBrand?.name}. Adding ${format.currency(face)} would exceed your ${format.currency(perMemberCap)} per-member cap (${format.currency(perMemberLeft)} left).`
                      : `Per-member cap on ${selectedBrand?.name} is ${format.currency(perMemberCap)}.`)}
                  </span>
                </div>
              )}
              {!cantReserve && exceedsCapacity && (
                <div className="flex items-start gap-2 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-md text-caption text-amber-800">
                  <AlertTriangle className="size-3.5 mt-0.5 shrink-0" />
                  <span>This exceeds your available capacity ({format.currency(stats.availableCapacity)}). You can still reserve.</span>
                </div>
              )}

              <Button
                className="w-full h-11 bg-brand-700 hover:bg-brand-800 text-white"
                onClick={() => setConfirmOpen(true)}
                disabled={cantReserve || !selectedCfg}
                data-testid="reserve-confirm-btn"
              >
                Reserve {format.currency(face)}
                <ArrowUpRight className="size-4 ml-2" strokeWidth={2.4} />
              </Button>

              <div className="text-caption text-ink-hint text-center leading-relaxed pt-1">
                Payment terms: <span className="text-ink-subtle font-medium">{deal.paymentTerms}</span>
              </div>
            </div>
          </div>
        </aside>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm reservation</DialogTitle>
            <DialogDescription>
              You&rsquo;re reserving {format.currency(face)} of face value on{" "}
              <span className="font-semibold text-ink">{selectedBrand?.name} &mdash; {deal.name}</span>.
              You have until {format.date(deal.deadline)} to submit cards for this brand.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-surface-alt border border-line rounded-md p-4 space-y-2.5 text-body-sm">
            <Row label="Brand" value={selectedBrand?.name || "—"} small />
            <Row label="Face reserved" value={format.currency(face)} />
            <Row label="Rate" value={`${selectedCfg?.rate || 0}%`} />
            <Row label="Expected payout" value={format.currency(expectedPayout)} accent />
            <Row label="Payment terms" value={deal.paymentTerms} small />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button onClick={confirm} className="bg-brand-700 hover:bg-brand-800 text-white">Confirm reservation</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Small primitives ──────────────────────────────────────────────────────

function ContentCard({ eyebrow, title, children, ...rest }) {
  return (
    <section className="bg-surface border border-line rounded-xl p-7" {...rest}>
      {eyebrow && <div className="eyebrow mb-2">{eyebrow}</div>}
      {title && <h2 className="font-brand text-h2 text-ink font-semibold mb-5">{title}</h2>}
      {children}
    </section>
  );
}

function HeroStat({ icon, label, value, urgent = false }) {
  return (
    <div className={`p-4 bg-brand-900/70 backdrop-blur-sm ${urgent ? "ring-1 ring-amber-300/40" : ""}`}>
      <div className="flex items-center gap-1.5 text-caption text-white/60">{icon}{label}</div>
      <div className={`mt-1 text-body font-bold tabular-nums ${urgent ? "text-amber-200" : "text-white"}`}>{value}</div>
    </div>
  );
}

function Step({ n, icon, title, body, last = false }) {
  return (
    <li className="flex gap-4">
      <div className="flex flex-col items-center">
        <div className="size-9 rounded-full bg-brand-50 border border-brand-100 text-brand-700 grid place-items-center font-brand font-bold tabular-nums">
          {n}
        </div>
        {!last && <div className="w-px flex-1 bg-line mt-1" />}
      </div>
      <div className="flex-1 pb-1">
        <div className="flex items-center gap-2 text-body-sm font-semibold text-ink">
          {icon && <span className="text-brand-700">{icon}</span>}
          {title}
        </div>
        <p className="mt-1 text-caption text-ink-muted leading-relaxed">{body}</p>
      </div>
    </li>
  );
}

function RuleGroup({ heading, tone, rules }) {
  const dotClass = tone === "red" ? "bg-red-500" : "bg-amber-500";
  const badgeTone = tone === "red" ? "danger" : "warning";
  return (
    <div>
      <div className="text-caption font-semibold text-ink-subtle uppercase tracking-wide mb-3">{heading}</div>
      <ul className="space-y-2.5">
        {rules.map((rule) => (
          <li key={rule.id} className="flex items-start gap-3 py-1.5">
            <span className={`mt-2 size-1.5 rounded-full ${dotClass} shrink-0`} />
            <div className="flex-1">
              <div className="text-body-sm text-ink font-medium">{rule.name}</div>
              <div className="text-caption text-ink-subtle leading-snug">{rule.description}</div>
            </div>
            <StatusBadge tone={badgeTone}>{rule.severity}</StatusBadge>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Row({ label, value, accent = false, small = false }) {
  return (
    <div className="flex justify-between items-baseline gap-3">
      <span className={`text-ink-subtle ${small ? "text-caption" : "text-caption"}`}>{label}</span>
      <span className={`tabular-nums ${accent ? "text-brand-700 font-bold text-h3" : "text-ink font-semibold text-body-sm"} ${small ? "text-caption font-medium" : ""}`}>{value}</span>
    </div>
  );
}
